import { filterClasses } from "@swivl-mono/helpers";
import { DropdownContainer, DropdownPosition, OptionsList } from "@swivl-mono/ui";
import { useState } from "react";
import { IoClose } from "react-icons/io5";

interface FilterChipProps {
  className?: string;
  groupLabel?: string;
  groupValue: string;
  labels: string[];
  selectedFilters?: string[];
  options?: { label: string; value: string }[];
  isMultiSelect?: boolean;
  onClearGroup: () => void;
  onSetFilters?: (groupValue: string, filters: string[]) => void;
}

const FilterChip = ({
  className,
  groupLabel,
  groupValue,
  labels,
  selectedFilters = [],
  options,
  isMultiSelect = true,
  onClearGroup,
  onSetFilters
}: FilterChipProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const isInteractive = !!options && options.length > 0;

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onClearGroup();
  };

  const triggerClassName = filterClasses(
    "body-sm inline-flex h-6.5 items-center gap-1 truncate rounded-[1px] border border-grey-200 bg-grey-50 pr-7 pl-2",
    isInteractive && "cursor-pointer transition-colors duration-150 ease-out hover:bg-grey-100",
    className
  );

  const triggerContent = (
    <>
      {groupLabel && <span className="text-grey-600">{groupLabel}</span>}
      <span>{labels.join(", ")}</span>
    </>
  );

  return (
    <div className="relative inline-flex items-center">
      {isInteractive ? (
        <DropdownContainer
          isOpen={isOpen}
          onOpenChange={setIsOpen}
          position={DropdownPosition.LEFT}
          styles={{ dropdown: { width: "160px" } }}
          trigger={<button className={triggerClassName}>{triggerContent}</button>}
        >
          <OptionsList
            items={options}
            searchQuery=""
            selectedValues={selectedFilters}
            handleToggleOption={(value) => {
              if (onSetFilters) {
                if (isMultiSelect) {
                  const nextFilters = selectedFilters.includes(value) ? selectedFilters.filter((f) => f !== value) : [...selectedFilters, value];
                  onSetFilters(groupValue, nextFilters);
                } else {
                  const nextFilters = selectedFilters.includes(value) ? [] : [value];
                  onSetFilters(groupValue, nextFilters);
                  setIsOpen(false);
                }
              }
            }}
            isMultiSelect={isMultiSelect}
          />
        </DropdownContainer>
      ) : (
        <span className={triggerClassName}>{triggerContent}</span>
      )}
      <button
        onClick={handleClose}
        className={filterClasses(
          "absolute top-0 right-0 z-10 flex h-full cursor-pointer items-center px-2",
          "transition-opacity duration-200 hover:opacity-70"
        )}
        aria-label="Clear filter"
      >
        <IoClose className="size-4 text-grey-600" />
      </button>
    </div>
  );
};

export { FilterChip };
